import React, { type FC, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { dateToLocaleOrDefault, getLanguageAsDateFnsLocaleObject } from '../../utils/utils'
import {
  DownloadMeterDataCsvBetweenSpecificDatesAction,
  DownloadMeterDataCsvFromStartDateAction
} from '../../redux/companies/companies-actions'
import { FormControl, Grid, InputLabel } from '@mui/material'

import { ModalCancelButton, ModalConfirmButton } from '../common/ui-components/Buttons.style'
import { ToggleDownloadDialogAction } from '../../redux/meter-table/meter-table-actions'
import { useTranslation } from 'react-i18next'
import { type TFunction } from 'i18next'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { type RootState } from '../../redux/store'
import { CsvType } from '../../constants/csv-type'
import {
  DownloadFileForAdminAction,
  DownloadFileWithinSpecificDatesForAdminAction
} from '../../redux/admin/admin-companies-actions'
import { type CustomerFilter, CustomerFilterType } from '../../redux/data/data-types'

export const GenerateTransferFileDialog: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const downloadDialogOpen = useAppSelector((state: RootState) => state.meterTable.downloadDialogOpen) ?? false
  const isAdmin = useAppSelector((state: RootState) => state.auth.isAdmin)
  const customerFilter = useAppSelector<CustomerFilter>((state: RootState) => state.data.customerFilter)

  const [downloadDateSelected, setDownloadDateSelected] = useState<Date>(new Date(Date.now()))
  const [rangeStart, setRangeStart] = useState<Date | undefined>()
  const [rangeEnd, setRangeEnd] = useState<Date | undefined>()
  const [downloadType, setDownloadType] = useState<CsvType>(CsvType.CSV)

  const errors = formErrors(downloadType, rangeStart, rangeEnd, t)
  const locale = getLanguageAsDateFnsLocaleObject()

  const generateFileForUser = (): void => {
    if (rangeStart == null || rangeEnd == null) {
      return
    }

    if (downloadType === CsvType.DATE_SPECIFIC) {
      dispatch(DownloadMeterDataCsvBetweenSpecificDatesAction({
        startDateSelected: rangeStart.valueOf(),
        endDateSelected: rangeEnd.valueOf()
      }))
    } else {
      dispatch(DownloadMeterDataCsvFromStartDateAction({
        downloadDateSelected: downloadDateSelected.valueOf(),
        downloadType
      }))
    }
  }

  const generateFileForAdmin = (): void => {
    if (customerFilter.type === CustomerFilterType.All) {
      console.warn('Cannot initiate download: customer filter setting is not specific to single company')
      return
    }

    if (downloadType === CsvType.DATE_SPECIFIC) {
      if (rangeStart == null || rangeEnd == null) {
        console.warn("Cannot initiate download type: 'rangeStart' or 'rangeEnd' is missing")
        return
      }
      dispatch(DownloadFileWithinSpecificDatesForAdminAction({
        startDateSelected: rangeStart.valueOf(),
        endDateSelected: rangeEnd.valueOf(),
        companyId: customerFilter.companyId
      }))
    } else {
      dispatch(DownloadFileForAdminAction({
        downloadDateSelected: downloadDateSelected.valueOf(),
        downloadType,
        companyId: customerFilter.companyId
      }))
    }
  }

  let showSelectedDownloadDates: string
  if (downloadType === CsvType.DATE_SPECIFIC) {
    showSelectedDownloadDates = t('transferfile_dialog.download_transferfile')
  } else {
    showSelectedDownloadDates = t('transferfile_dialog.download_transferfile') + ` - ${dateToLocaleOrDefault(downloadDateSelected)}`
  }

  const onConfirmationButtonClick = (): void => {
    if (isAdmin && customerFilter.type !== CustomerFilterType.All) {
      generateFileForAdmin()
    } else {
      generateFileForUser()
    }
  }

  return (
        <Dialog
            data-testid="dataDialog"
            open={downloadDialogOpen}
            onClose={() => dispatch(ToggleDownloadDialogAction())}
            maxWidth={downloadType === CsvType.DATE_SPECIFIC ? 'md' : 'xs'}
            fullWidth>
            <DialogTitle>{`${showSelectedDownloadDates}`}</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    {
                        downloadType === CsvType.DATE_SPECIFIC
                          ? (
                                <Grid item xs={12} md={12} style={{ marginLeft: '-1.5em', display: 'flex' }}>
                                    <Grid item xs={6}>
                                        <label style={{ margin: '1.5em' }}>{t('transferfile_dialog.date_range_start')}</label>
                                        <DayPicker data-testid="startDatePicker" selected={rangeStart}
                                                   onDayClick={setRangeStart} defaultMonth={downloadDateSelected}
                                                   locale={locale}/>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <label style={{ margin: '1.5em' }}>{t('transferfile_dialog.date_range_end')}</label>
                                        <DayPicker data-testid="endDatePicker" selected={rangeEnd} onDayClick={setRangeEnd}
                                                   defaultMonth={downloadDateSelected} locale={locale}/>
                                    </Grid>
                                </Grid>
                            )
                          : (
                                <Grid item xs={12} style={{ marginLeft: '-1.5em' }}>
                                    <DayPicker selected={downloadDateSelected} onDayClick={setDownloadDateSelected}
                                               defaultMonth={downloadDateSelected} locale={locale}/>
                                </Grid>)
                    }
                    {
                        errors.length !== 0 && (
                            <Grid item xs={12}>
                                <span style={{
                                  padding: '1em',
                                  marginBottom: '1em',
                                  backgroundColor: '#d32f2f',
                                  borderRadius: '4px',
                                  display: 'inline-block'
                                }}>{errors.join(' , ')}</span>
                            </Grid>
                        )
                    }
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>{t('transferfile_dialog.type')}</InputLabel>
                            <Select
                                data-testid="selectModal"
                                variant="standard"
                                value={downloadType}
                                onChange={(e) => {
                                  setDownloadType(e.target.value as CsvType)
                                }}
                            >
                                <MenuItem value={CsvType.CSV}>CSV</MenuItem>
                                <MenuItem value={CsvType.CSV_FIN}>CSV (FIN)</MenuItem>
                                <MenuItem
                                    value={CsvType.COMPANY_SPECIFIC}>{t('transferfile_dialog.transferfile')}</MenuItem>
                                <MenuItem data-testid="menuItem"
                                          value={CsvType.DATE_SPECIFIC}>{t('transferfile_dialog.readings_for_range')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <ModalCancelButton
                    onClick={() => {
                      dispatch(ToggleDownloadDialogAction())
                    }}
                >
                    {t('common.actions.cancel')}
                </ModalCancelButton>
                <ModalConfirmButton
                    data-testid="modalConfirmButton"
                    color='secondary' disabled={errors.length !== 0}
                    onClick={onConfirmationButtonClick}
                >
                    {t('common.actions.download')}
                </ModalConfirmButton>
            </DialogActions>
        </Dialog>
  )
}

/* Helper Functions */

function formErrors(downloadType: CsvType, rangeStart: Date | undefined, rangeEnd: Date | undefined, t: TFunction): string[] {
  const errors: string[] = []
  if (downloadType === CsvType.DATE_SPECIFIC) {
    if (rangeStart === undefined) {
      const err: string = t('errors.select_start_date')
      errors.push(err)
    }
    if (rangeEnd === undefined) {
      const err: string = t('errors.select_start_date')
      errors.push(err)
    }
    if (rangeStart !== undefined && rangeEnd !== undefined && rangeStart > rangeEnd) {
      const err: string = t('date_range_invalid')
      errors.push(err)
    }
  }
  return errors
}
